import { GenericSubmissionField } from './GenericSubmissionField';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';

interface Field {
  name: string;
  /**
   * If you want to create a custom wrapper around the field
   */
  renderMethod?: (fieldElement: React.ReactElement) => React.ReactElement;
  // TODO: optional could add "additionalConditions" prop
  customCopies?: {
    fieldLabel?: string;
    diffModalTitle?: string;
  };
}

interface GroupRendererProps {
  fields: Field[];
  children: (renderedFields: React.ReactNode[]) => React.ReactNode;
  showExternalDependencyEmptyState?: boolean;
  showSubtitle?: boolean;
  isUpdateRequired?: boolean;
  isRevisionReview?: boolean;
}

const GroupRenderer: React.FC<GroupRendererProps> = ({
  fields,
  children,
  showExternalDependencyEmptyState = false,
  showSubtitle = true,
  isUpdateRequired = true,
  isRevisionReview = false
}) => {
  const { getFieldState } = useSubmissionFields();

  const renderedFields = fields.map(field => {
    const ffd = getFieldState(field.name);
    if (!ffd) return null;

    if (
      'externalFieldDependency' in ffd.formFieldDefinition.params &&
      ffd.externalDependency?.pathValue !==
        ffd.formFieldDefinition.params.externalFieldDependency?.value &&
      !showExternalDependencyEmptyState
    )
      return null;

    const fieldElement = (
      <GenericSubmissionField
        key={field.name}
        fieldName={field.name}
        showExternalDependencyEmptyState={showExternalDependencyEmptyState}
        showSubtitle={showSubtitle}
        customCopies={field.customCopies}
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
      />
    );
    return field.renderMethod ? field.renderMethod(fieldElement) : fieldElement;
  });

  // Render the group if at least one field exists
  if (renderedFields.every(field => field === null)) return null;

  if (children) {
    return <>{children(renderedFields)}</>;
  }

  return <>{renderedFields}</>;
};

export default GroupRenderer;
